import { motion } from "framer-motion";
import { Calendar, CheckCircle } from "lucide-react";

export const BotCourse = () => {
  const botSteps = ["Move forward", "Turn left or right", "Stop", "Repeat", "If something happens, do something else"];
  const toolsUsed = [
    "Programmable bots (e.g., Cody Rocky)",
    "Direction cards",
    "Floor grids",
    "Maps",
    "Arrows and commands",
    "Hands-on challenges",
  ];

  return (
    <div className="w-full bg-gray-50 min-h-screen">
      <div className="bg-orange-500 text-white py-20 px-4 text-center">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold mb-4"
        >
          Bot Programming
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-xl max-w-2xl mx-auto opacity-90"
        >
          Course 4: Program real bots with logic, sequences, and challenges.
        </motion.p>
      </div>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">What is Bot Programming?</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Bot programming is the process of giving step-by-step instructions to a bot (robot or character) so it
              knows what to do, when to do it, and how to react. We translate our instructions into a program and
              upload it to the robot, making it perform tasks exactly as planned.
            </p>

            <div className="flex flex-wrap gap-6 mb-8 text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-500" />
                <span>Duration: 3 months</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-orange-500" />
                <span>Age: 6-12 years</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-orange-500" />
                <span>Prerequisite: Scratch Programming</span>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-800 mb-4">How does bot programming work?</h3>
            <p className="text-gray-600 leading-relaxed mb-4">A bot follows instructions such as:</p>
            <ul className="space-y-3 mb-6">
              {botSteps.map((step) => (
                <li key={step} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 shrink-0" />
                  <span className="text-gray-700">{step}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-2xl bg-orange-50 border border-orange-100 p-6 text-sm text-gray-700">
              <p className="font-semibold mb-2">Example sequence</p>
              <p>Move forward → Turn right → Move 2 steps → If there is a wall, stop.</p>
            </div>
          </div>

          <div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="/images/little-boy-kid-child-constructor-checking-technical-toy.jpg"
                alt="Child building a robot"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-orange-500 font-bold tracking-wider uppercase text-sm">Screen-Free Learning</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
              Bot Programming at Code Riite
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Instead of screens, kids see the bot move in real life, helping them understand how computers follow
              instructions with clarity and confidence.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {toolsUsed.map((tool) => (
              <div
                key={tool}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <p className="text-gray-800 font-semibold">{tool}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BotCourse;
